
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { EnhancedUserProfile, enhanceProfilesWithSpotifyData } from './ProfileDataService';
import { mockProfiles } from './mockData';

export function useMatchProfiles() {
  const { toast } = useToast();
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [likedProfiles, setLikedProfiles] = useState<string[]>([]);
  const [passedProfiles, setPassedProfiles] = useState<string[]>([]);
  const [key, setKey] = useState(0); // Key to force re-render of SwipeCard
  const [profiles, setProfiles] = useState<EnhancedUserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load and enhance profiles with Spotify data
  useEffect(() => {
    const loadEnhancedProfiles = async () => {
      setIsLoading(true);
      
      try {
        console.log("Starting profile loading process");
        
        // Step 1: First enhance all profiles with Spotify data before any reordering
        const enhancedProfiles = await enhanceProfilesWithSpotifyData([...mockProfiles]);
        console.log("Initial enhanced profiles:", enhancedProfiles.map(p => p.name));
        
        // Step 2: Find Burna Boy's fully enhanced profile 
        const burnaIndex = enhancedProfiles.findIndex(profile => profile.userId === '6');
        
        if (burnaIndex !== -1) {
          console.log("Found Burna Boy at index:", burnaIndex);
          // Get Burna Boy's fully enhanced profile with all the Spotify data and match score
          const burnaProfile = enhancedProfiles[burnaIndex];
          console.log("Burna's profile data:", {
            name: burnaProfile.name,
            hasSpotify: !!burnaProfile.spotifyProfile,
            hasMatchScore: !!burnaProfile.matchScore
          });
          
          // Remove Burna Boy's profile from the array
          enhancedProfiles.splice(burnaIndex, 1);
          
          // Create a new array with Burna Boy explicitly as the first profile
          const reorderedProfiles = [burnaProfile, ...enhancedProfiles.filter(p => p.userId !== '1')];
          console.log("Final profile order:", reorderedProfiles.map(p => p.name));
          
          // Set the reordered profiles
          setProfiles(reorderedProfiles);
          
          // Ensure we're starting at index 0 (Burna Boy)
          setCurrentProfileIndex(0);
        } else {
          console.warn("Burna Boy's profile not found in enhanced profiles");
          setProfiles(enhancedProfiles);
        }
      } catch (error) {
        console.error("Error loading profiles:", error);
        toast({
          title: "Error",
          description: "Failed to load musician profiles",
        });
        
        // Fallback to basic mock data with manual Burna Boy prioritization
        const fallbackProfiles = [...mockProfiles];
        const burnaIndex = fallbackProfiles.findIndex(profile => profile.userId === '6');
        if (burnaIndex !== -1) {
          const burnaProfile = fallbackProfiles.splice(burnaIndex, 1)[0];
          fallbackProfiles.unshift(burnaProfile);
        }
        setProfiles(fallbackProfiles);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadEnhancedProfiles();
  }, [toast]);

  // Reset key when profile changes to force SwipeCard re-render
  useEffect(() => {
    setKey(prevKey => prevKey + 1);
  }, [currentProfileIndex]);

  // Handle like action
  const handleLike = (profileId: string) => {
    if (!likedProfiles.includes(profileId)) {
      setLikedProfiles([...likedProfiles, profileId]);
      
      toast({
        title: "Liked!",
        description: `You liked ${profiles.find(p => p.userId === profileId)?.name}'s profile`,
      });
      
      if (Math.random() > 0.5) {
        setTimeout(() => {
          toast({
            title: "It's a match!",
            description: `${profiles.find(p => p.userId === profileId)?.name} liked you back!`,
            duration: 5000,
          });
        }, 1500);
      }
    }
    
    // Move to next profile
    if (currentProfileIndex < profiles.length - 1) {
      setCurrentProfileIndex(prevIndex => prevIndex + 1);
    }
  };
  
  // Handle pass action
  const handlePass = (profileId: string) => {
    if (!passedProfiles.includes(profileId)) {
      setPassedProfiles([...passedProfiles, profileId]);
    }
    
    // Move to next profile
    if (currentProfileIndex < profiles.length - 1) {
      setCurrentProfileIndex(prevIndex => prevIndex + 1);
    }
  };

  // Current profile based on index
  const currentProfile = profiles[currentProfileIndex];
  
  // Debug check to verify current profile and its data
  if (currentProfile) {
    console.log("Currently displaying profile:", currentProfile.name, "userId:", currentProfile.userId);
    console.log("Profile data checks:", {
      hasSpotifyData: !!currentProfile.spotifyProfile,
      hasMatchScore: !!currentProfile.matchScore,
      topTracks: currentProfile.spotifyProfile?.topTracks?.length || 0
    });
  }

  return {
    profiles,
    currentProfile,
    currentProfileIndex,
    isLoading,
    swipeKey: key,
    handleLike,
    handlePass
  };
}
